import {Component} from '@angular/core';
import {NativeService} from '../../_service/native.service';
import {NGXLogger} from "ngx-logger";
import {UserService} from "../../domain/user/service/user.service";
import {User} from "../../domain/user/model/user";
import {AnalysisService} from "../../analysis/_service/analysis.service";
import {HouseService} from "../../domain/house/service/house.service";
import {CouponService} from "../../domain/coupon/coupon.service";
import {NoticeService} from "../../domain/notice/notice.service";
import {Router} from "@angular/router";
import {catchError, EMPTY, expand, of, reduce} from 'rxjs';

interface MainNotice {
  id: number;
  title: string;
  type: 'NOTICE' | 'UPDATE';
  writeAt: string | Date;
}

declare let window: any;

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class MainComponent {
  showStoreLink = false;
  isLogin: boolean = false;
  counts = {
    house: 0,
    analysis: 0,
    analysisPending: 0
  }
  couponCount = 0;
  hasAnalysisFreeCoupon = false;
  notices: MainNotice[] = [];
  updates: MainNotice[] = [];
  mainMapHouses: any[] = [];
  private mainMapRequested = false;
  private mainMapChangedAtByHouseId = new Map<string, any>();

  constructor(private readonly log: NGXLogger,
              private readonly router: Router,
              private readonly userService: UserService,
              private readonly houseService: HouseService,
              private readonly noticeService: NoticeService,
              private readonly analysisService: AnalysisService,
              private readonly couponService: CouponService,
              private readonly nativeService: NativeService) {
    this.showStoreLink = !this.nativeService.isNative();

    this.getMainNotices();

    this.userService.changeUser()
      .subscribe((user: User | undefined) => {
        this.isLogin = user !== undefined;
        this.initSummaryData()
      });

    // 홈의 보조 정보는 각 카드가 기본값을 갖고 있어 전체 화면 로딩 없이 갱신한다.
    this.userService.checkSession(true)
      .subscribe(result => {
        this.isLogin = result;
        this.initSummaryData()
      });
  }

  moveNoticeDetail(notice: MainNotice) {
    this.router.navigate(['/info/notice/detail'], {queryParams: {id: notice.id}});
  }

  openPlayStore() {
    window.open('https://play.google.com/store/apps/details?id=kr.co.lawalliance.checkteam')
  }

  openAppStore() {
    if (this.nativeService.isiOS) {
      window.open('itms-apps://itunes.apple.com/kr/app/apple-store/id1578403569?mt=8');
    } else {
      window.open('https://itunes.apple.com/kr/app/apple-store/id1578403569?mt=8');
    }
  }

  private initSummaryData() {
    if (this.isLogin) {
      this.loadMainMapData();
      this.analysisService.getCounts(true)
        .subscribe(response => {
          this.log.debug('권리분석 수', response);
          this.counts.analysis = response.completeCount + response.pendingCount;
          this.counts.analysisPending = response.pendingCount;
        })

      this.couponService.hasAnalysisFreeCoupon(true)
        .subscribe((exists: boolean) => {
          this.log.debug('무료 권리분석 쿠폰 보유 여부', exists);
          this.hasAnalysisFreeCoupon = exists;
        })

      this.couponService.getCoupons('ALL', true)
        .subscribe(coupons => {
          this.couponCount = Object.values(coupons ?? {})
            .reduce((total: number, count) => total + (Number(count) || 0), 0);
        })

      this.houseService.getHouseSummary(true)
        .subscribe(response => {
          this.log.debug('부동산 수', response);
          this.counts.house = response.myHouse + response.openHouse + response.openSubscribe;
        })
    } else {
      this.mainMapRequested = false;
      this.mainMapHouses = [];
      this.mainMapChangedAtByHouseId.clear();
    }
  }

  private loadMainMapData(): void {
    if (this.mainMapRequested) {
      return;
    }
    this.mainMapRequested = true;

    // 지도용 축약 응답을 먼저 표시하고, 변경일자는 별도 요청 완료 후 색상만 갱신한다.
    this.houseService.getMyHouses({searchType: 'MAP'}, true).pipe(
      catchError(() => of([]))
    ).subscribe(houses => {
      this.mainMapHouses = (houses ?? []).map((house: any) => ({
        ...house,
        changedAt: this.mainMapChangedAtByHouseId.get(String(house.id)) ?? house.changedAt
      }));
    });

    const changedHouseRequest = {
      searchType: 'PAGING', page: 0, size: 100, type: 'ALL', state: 'CHECKED',
      order: 'CHANGE', reverse: false, search: ''
    };
    this.houseService.getMyHouses(changedHouseRequest, true).pipe(
      expand(response => response?.last === false
        ? this.houseService.getMyHouses({...changedHouseRequest, page: response.number + 1}, true)
        : EMPTY),
      reduce((houses: any[], response: any) => houses.concat(response?.content ?? []), []),
      catchError(() => of([]))
    ).subscribe(houses => {
      this.mainMapChangedAtByHouseId = new Map(houses
        .filter((house: any) => !house.expired && house.subscribeOwner)
        .map((house: any) => [String(house.id), house.changedAt]));
      this.mainMapHouses = this.mainMapHouses.map((house: any) => ({
        ...house,
        changedAt: this.mainMapChangedAtByHouseId.get(String(house.id)) ?? null
      }));
    });

  }

  private getMainNotices() {
    this.noticeService.getMainPageNotice(true)
      .subscribe((response: MainNotice[] | undefined) => {
        if (Array.isArray(response)) {
          this.notices = response.filter(notice => notice.type === 'NOTICE');
          this.updates = response.filter(notice => notice.type === 'UPDATE');
        }
      });
  }
}
